import { FormEvent, useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "../../QRCodeCanvas";
import "./JoinGameModal.scss";
import ChessButton from "../../../visual/ChessButton/ChessButton";
import useSoundEffect from "../../../../hooks/useSoundEffect";
import { MULTIPLAYER } from "../../../../utils/constants";

interface JoinGameModalProps {
    lobbyId: string;
    onClose: () => void;
    onJoin: (username: string) => void;
    generateRandomName: (setUsername: (name: string) => void) => Promise<void>;
    isGenerating: boolean;
}

export function JoinGameModal({
    lobbyId,
    onClose,
    onJoin,
    generateRandomName,
    isGenerating,
}: JoinGameModalProps) {
    const modalRef = useRef<HTMLDivElement>(null);
    const { playSelectAudio } = useSoundEffect();
    const [username, setUsername] = useState("");

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onClose]);

    // Disable keyboard while modal is open
    useEffect(() => {
        const handleKeydown = (e: KeyboardEvent) => {
            e.stopPropagation();
        };
        window.addEventListener("keydown", handleKeydown, true);
        return () => {
            window.removeEventListener("keydown", handleKeydown, true);
        };
    }, []);

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        playSelectAudio();
        const trimmed = username.trim();
        if (!trimmed) return;

        const search = new URLSearchParams(window.location.search);
        search.set(MULTIPLAYER.LOBBY_ID_KEY, lobbyId);
        window.history.replaceState({}, "", `?${search.toString()}`);

        onJoin(trimmed);
    };

    return (
        <div className="modal-backdrop">
            <div className="modal" ref={modalRef}>
                <button
                    aria-label="Close Modal"
                    className="close-modal-btn"
                    onClick={() => {
                        playSelectAudio();
                        onClose();
                    }}
                >
                    <img src="/images/close-circle-gold.webp" alt="" className="close-modal-img" />
                </button>
                <form onSubmit={onSubmit}>
                    <p className="modal-text">Enter your name</p>
                    <div className="content">
                        <input
                            className="name-input"
                            id="username"
                            type="text"
                            placeholder="Enter username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            autoFocus
                        />
                        <ChessButton
                            className="gen-btn"
                            tabIndex={-1}
                            type="button"
                            disabled={isGenerating}
                            onClick={async () => {
                                await generateRandomName(setUsername);
                            }}
                        >
                            Generate Name
                        </ChessButton>
                    </div>
                    <div className="small-qr-cont">
                        <QRCodeCanvas lobbyId={lobbyId} id="qr-code-small" />
                    </div>
                    <ChessButton type="submit" className="join-btn">
                        Join Game
                    </ChessButton>
                </form>
            </div>
        </div>
    );
}
